import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { FiInfo, FiArrowRight, FiLock } from 'react-icons/fi';
import SEO from '../components/SEO';
import styles from './Calculators.module.css';
import {
  CALCULATORS,
  CALCULATORS_HERO,
  CALC_MODULES,
  CALC_PLANS,
  CALC_SCENARIOS,
  CALC_ADOPTIONS,
  CALC_BUFFERS,
  CalcFormat,
  CalcProfession,
} from '../config/content';
import { REGION } from '../config/region';
import {
  calculate,
  getCurrencies,
  CalculatorPayload,
  CalculatorReport,
} from '../services/calculatorService';

const FALLBACK_CURRENCIES = REGION.calculator.currencies;
/* Figures in a report can be in any currency the visitor picks, so they are
   written in the locale that names it unambiguously (under 'en', A$ vs US$). */
const NUMBER_LOCALE = REGION.formats.moneyLocale;
const CARD_LAYOUT_TRANSITION = { duration: 0.45, ease: [0.4, 0, 0.2, 1] as const };

type Scenario = 'conservative' | 'expected' | 'optimistic';
type Adoption = 3 | 6 | 12;
/* 'auto' lets the backend price every plan at the firm's volume and pick the
   cheapest, which is the honest default — the answer differs by profession. */
type PlanChoice = string;

/* One row of the team breakdown: a person, or several on the same terms.
   Kept as strings while editing so a half-typed number is not clobbered. */
interface TeamRow {
  id: number;
  role: string;
  count: string;
  days: number; // days a week; 5 = full-time
  rate: string;
}

const OTHER_ROLE = '__other__';

const WORK_PATTERNS = [
  { days: 5, label: 'Full-time' },
  { days: 4, label: '4 days' },
  { days: 3, label: '3 days' },
  { days: 2, label: '2 days' },
  { days: 1, label: '1 day' },
];

/**
 * Headcount, FTE and average rate for a team breakdown — the same arithmetic
 * the backend does, so the fields it replaces can show the figure in use. A
 * row with no rate takes the top-level one (pro-rated for a salary).
 */
function summariseTeam(
  rows: TeamRow[],
  kind: 'hourly' | 'salary' | undefined,
  fallbackRate: number
): { headcount: number; fte: number; rate: number } {
  let headcount = 0;
  let fte = 0;
  let weighted = 0;
  rows.forEach((r) => {
    const count = Math.max(0, Math.floor(parseFloat(r.count) || 0));
    const share = r.days / 5;
    const raw = parseFloat(r.rate);
    headcount += count;
    fte += count * share;
    if (kind === 'salary') {
      weighted += count * (Number.isFinite(raw) ? raw : fallbackRate * share);
    } else {
      weighted += count * share * (Number.isFinite(raw) ? raw : fallbackRate);
    }
  });
  return { headcount, fte, rate: fte ? weighted / fte : 0 };
}

/** Everything in the advanced panel, passed around as one object. */
interface Advanced {
  scenario: Scenario;
  adoption: Adoption;
  plan: PlanChoice;
  modules: string[];
  /* Safety margin taken off the savings — never off the cost — before they are
     shown. Sent to the backend as `prudence_buffer`. */
  buffer: number;
  /* Person-by-person breakdown; null when the flat headcount is in use. */
  team: TeamRow[] | null;
}

const ALL_MODULES = CALC_MODULES.map((m) => m.key);
/* How many modules the calculator backend models (ModuleKey). */
const BACKEND_MODULE_COUNT = 3;

/* The value each advanced control starts on, taken from the option marked
   `default` so the settings and the copy explaining them can never drift. */
const DEFAULT_SCENARIO = (CALC_SCENARIOS.find((s) => s.default)?.key ?? 'expected') as Scenario;
const DEFAULT_ADOPTION = (CALC_ADOPTIONS.find((a) => a.default)?.key ?? 6) as Adoption;
const DEFAULT_BUFFER = CALC_BUFFERS.find((b) => b.default)?.key ?? 0.15;
const DEFAULT_PLAN: PlanChoice = 'auto';

/* ── Formatting helpers ───────────────────────────────────────── */
function formatValue(value: number | null | undefined, format: CalcFormat, currency: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  switch (format) {
    case 'money':
      return new Intl.NumberFormat(NUMBER_LOCALE, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(value);
    case 'hours':
      return new Intl.NumberFormat(NUMBER_LOCALE, { maximumFractionDigits: 0 }).format(value);
    case 'number':
      return new Intl.NumberFormat(NUMBER_LOCALE, { maximumFractionDigits: 1 }).format(value);
    case 'percent':
      return `${new Intl.NumberFormat(NUMBER_LOCALE, { maximumFractionDigits: 1 }).format(value)}%`;
    case 'multiple':
      return `${new Intl.NumberFormat(NUMBER_LOCALE, { maximumFractionDigits: 1 }).format(value)}×`;
    case 'months':
      return value < 1
        ? '< 1 mo'
        : `${new Intl.NumberFormat(NUMBER_LOCALE, { maximumFractionDigits: 1 }).format(value)} mo`;
    default:
      return String(value);
  }
}

/* ── Build the initial form state from a profession's field defaults ── */
function defaultsFor(profession: CalcProfession): Record<string, string> {
  const state: Record<string, string> = {};
  [...profession.fields, ...profession.usageFields].forEach((f) => {
    state[f.name] = String(f.default);
  });
  return state;
}

/**
 * The (i) that explains a term in plain English.
 *
 * The bubble is portalled to <body> with fixed positioning, so no panel can
 * clip it — the left column scrolls on its own and the advanced panel hides
 * its overflow while animating. It is placed next to the icon and clamped to
 * the viewport. `align` picks below (the narrow input column) or above (the
 * results table).
 */
const TOOLTIP_GAP = 8;
const VIEWPORT_MARGIN = 12;

const Info: React.FC<{ text: string; align?: 'up' | 'down' }> = ({ text, align = 'up' }) => {
  const iconRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [pos, setPos] = useState<React.CSSProperties | null>(null);
  // Long, multi-line explanations get a wider bubble so the bullets stay readable.
  const width = text.includes('\n') ? 340 : align === 'down' ? 260 : 240;

  const show = () => {
    if (iconRef.current) setAnchor(iconRef.current.getBoundingClientRect());
  };
  const hide = () => {
    setAnchor(null);
    setPos(null);
  };

  // Place the bubble once it has rendered and its real height is known: on the
  // preferred side if it fits, otherwise whichever side has more room, and
  // always clamped inside the viewport.
  useLayoutEffect(() => {
    if (!anchor || !tipRef.current) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(width, vw - VIEWPORT_MARGIN * 2);
    const h = tipRef.current.offsetHeight;
    const preferred = align === 'down' ? anchor.left - 10 : anchor.left + anchor.width / 2 - w / 2;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(preferred, vw - w - VIEWPORT_MARGIN));
    const roomAbove = anchor.top - TOOLTIP_GAP - VIEWPORT_MARGIN;
    const roomBelow = vh - anchor.bottom - TOOLTIP_GAP - VIEWPORT_MARGIN;
    const fitsAbove = h <= roomAbove;
    const fitsBelow = h <= roomBelow;
    const below =
      align === 'down' ? fitsBelow || (!fitsAbove && roomBelow >= roomAbove)
                       : !fitsAbove && (fitsBelow || roomBelow > roomAbove);
    const rawTop = below ? anchor.bottom + TOOLTIP_GAP : anchor.top - TOOLTIP_GAP - h;
    const top = Math.max(VIEWPORT_MARGIN, Math.min(rawTop, vh - h - VIEWPORT_MARGIN));
    setPos({ top, left, width: w });
  }, [anchor, align, width]);

  // A scroll anywhere (page or the input column) moves the icon: close rather
  // than leave the bubble floating in the wrong place.
  useEffect(() => {
    if (!anchor) return;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [anchor]);

  return (
    <span
      ref={iconRef}
      className={styles.infoWrap}
      tabIndex={0}
      role="note"
      aria-label={text}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <FiInfo className={styles.infoIcon} />
      {anchor &&
        createPortal(
          <span
            ref={tipRef}
            className={styles.tooltipFloating}
            // Measured off-screen first, then placed — no flash in the wrong spot.
            style={pos ?? { top: 0, left: 0, width: Math.min(width, window.innerWidth - VIEWPORT_MARGIN * 2), visibility: 'hidden' }}
            aria-hidden="true"
          >
            {text}
          </span>,
          document.body
        )}
    </span>
  );
};

const Calculators: React.FC = () => {
  const { profession: slug } = useParams<{ profession: string }>();
  const config = slug ? CALCULATORS[slug] : undefined;

  const [form, setForm] = useState<Record<string, string>>(() =>
    config ? defaultsFor(config) : {}
  );
  const [report, setReport] = useState<CalculatorReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [currencies, setCurrencies] = useState<string[]>(FALLBACK_CURRENCIES);
  const [revision, setRevision] = useState(0);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [scenario, setScenario] = useState<Scenario>(DEFAULT_SCENARIO);
  // 6 mo matches a typical onboarding; user can pick faster (3) or slower (12).
  const [adoption, setAdoption] = useState<Adoption>(DEFAULT_ADOPTION);
  // 'auto' asks the backend which plan is cheapest at this firm's volume.
  const [plan, setPlan] = useState<PlanChoice>(DEFAULT_PLAN);
  const [modules, setModules] = useState<string[]>(ALL_MODULES);
  // Savings are shown after this haircut. Starts at the published default so
  // nobody has to opt in to a defensible number.
  const [buffer, setBuffer] = useState<number>(DEFAULT_BUFFER);
  const [team, setTeam] = useState<TeamRow[] | null>(null);
  // Which way the headcount is entered. Rows are kept when switching back to a
  // single number, so flipping the switch never throws work away.
  const [teamOn, setTeamOn] = useState(false);
  const activeTeam = teamOn ? team : null;
  const rowIdRef = useRef(0);
  const callIdRef = useRef(0);

  const inputCurrency =
    form.currency || (config?.fields.find((f) => f.name === 'currency')?.default as string) || REGION.currency;
  // Format figures in the currency the report was actually computed in, so the
  // numbers never disagree with their symbol if the dropdown changes pre-recalc.
  const currency = report?.currency ?? inputCurrency;

  const buildPayload = useCallback(
    (
      cfg: CalcProfession,
      state: Record<string, string>,
      advanced: Advanced
    ): CalculatorPayload => {
      const payload: CalculatorPayload = {};
      [...cfg.fields, ...cfg.usageFields].forEach((f) => {
        const raw = state[f.name];
        if (f.type === 'currency-select') {
          payload[f.name] = raw || REGION.currency;
        } else {
          const num = parseFloat(raw);
          payload[f.name] = Number.isFinite(num) ? num : 0;
        }
      });
      payload.scenario = advanced.scenario;
      payload.adoption_months_to_full = advanced.adoption;
      payload.prudence_buffer = advanced.buffer;
      // Omit billing_plan entirely on 'auto' so the backend recommends one.
      if (advanced.plan !== 'auto') payload.billing_plan = advanced.plan;
      // Only send a module list when it is a real narrowing; the backend reads
      // an absent list as "all three", which is also what a full list means.
      // A market that offers fewer than the backend's three always narrows.
      if (advanced.modules.length && advanced.modules.length < BACKEND_MODULE_COUNT) {
        payload.modules = advanced.modules;
      }
      Object.assign(payload, cfg.fixedInputs);
      // A team breakdown replaces the headcount and the average rate on the
      // backend; the headcount is still sent so the request validates.
      const rows = (advanced.team ?? []).filter((r) => (parseFloat(r.count) || 0) >= 1);
      if (rows.length) {
        const t = cfg.team;
        payload.team = rows.map((r) => {
          const rate = parseFloat(r.rate);
          return {
            role: r.role.trim() || null,
            count: Math.floor(parseFloat(r.count)),
            fte: r.days / 5,
            rate: t.rateField && Number.isFinite(rate) ? rate : null,
          };
        });
        payload[t.headcountField] = rows.reduce(
          (n, r) => n + Math.floor(parseFloat(r.count)),
          0
        );
      }
      return payload;
    },
    []
  );

  const runCalc = useCallback(
    async (
      cfg: CalcProfession,
      state: Record<string, string>,
      advanced: Advanced,
      opts: { silent?: boolean } = {}
    ) => {
      const myId = ++callIdRef.current;
      // Silent recalcs (toggle-driven) keep the Calculate ROI button label
      // stable — only explicit submits flip it to "Calculating…".
      if (!opts.silent) setLoading(true);
      setError(null);
      try {
        const result = await calculate(cfg.service, buildPayload(cfg, state, advanced));
        if (callIdRef.current !== myId) return; // a newer call superseded us
        setReport(result);
        setRevealed(false);
        setRevision((r) => r + 1);
      } catch (err) {
        if (callIdRef.current !== myId) return;
        setError(err instanceof Error ? err.message : 'Something went wrong.');
        setReport(null);
      } finally {
        if (!opts.silent && callIdRef.current === myId) setLoading(false);
      }
    },
    [buildPayload]
  );

  // Load supported currencies once.
  useEffect(() => {
    let alive = true;
    getCurrencies()
      .then((res) => {
        const list = Object.keys(res.currencies || {});
        if (alive && list.length) setCurrencies(list);
      })
      .catch(() => {/* keep fallback */});
    return () => {
      alive = false;
    };
  }, []);

  // Reset + auto-calculate whenever the profession (route) changes.
  useEffect(() => {
    if (!config) return;
    const defaults = defaultsFor(config);
    setForm(defaults);
    setTeam(null);
    setTeamOn(false);
    runCalc(config, defaults, { scenario, adoption, plan, modules, buffer, team: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Re-run on advanced toggle change — the backend owns the math now, so we
  // refetch instead of scaling client-side. Silent so the Calculate ROI label
  // doesn't flicker. Skipped on first render; the slug effect handles that.
  useEffect(() => {
    if (!config || !report) return;
    runCalc(config, form, { scenario, adoption, plan, modules, buffer, team: activeTeam }, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario, adoption, plan, modules, buffer]);

  const handleChange = (name: string, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (config) runCalc(config, form, { scenario, adoption, plan, modules, buffer, team: activeTeam });
  };

  /* Turning the breakdown on seeds it with the flat figures, so the first row
     is the team as already entered and the user only adds what differs. */
  const newRow = (partial: Partial<TeamRow> = {}): TeamRow => ({
    id: ++rowIdRef.current,
    role: '',
    count: '1',
    days: 5,
    rate: '',
    ...partial,
  });
  const startTeam = () => {
    if (!config) return;
    setTeamOn(true);
    if (team?.length) return;
    const t = config.team;
    setTeam([
      newRow({
        role: t.roles[0] ?? '',
        count: form[t.headcountField] || '1',
        rate: t.rateField ? form[t.rateField] ?? '' : '',
      }),
    ]);
  };
  const updateRow = (id: number, patch: Partial<TeamRow>) =>
    setTeam((rows) => (rows ?? []).map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id: number) =>
    setTeam((rows) => {
      const next = (rows ?? []).filter((r) => r.id !== id);
      if (!next.length) setTeamOn(false);
      return next.length ? next : null;
    });

  const teamSummary = useMemo(() => {
    if (!activeTeam || !config) return null;
    const t = config.team;
    const fallback = t.rateField ? parseFloat(form[t.rateField]) || 0 : 0;
    return summariseTeam(activeTeam, t.rateKind, fallback);
  }, [activeTeam, config, form]);

  /* At least one module has to stay on — a firm using none of them has no
     benefit to model, and an empty list would silently mean "all three". */
  const toggleModule = (key: string) => {
    setModules((prev) =>
      prev.includes(key)
        ? prev.length > 1
          ? prev.filter((m) => m !== key)
          : prev
        : [...prev, key]
    );
  };

  const headlineMultiple = useMemo(() => {
    if (!report || !config) return null;
    const v = report.headline[config.headlineKey];
    return typeof v === 'number' ? v : null;
  }, [report, config]);

  const planComparison = useMemo(
    // Only the plans this market sells (CALC_PLANS): the backend can price a
    // plan the market does not offer.
    () =>
      (report?.assumptions?.plan_comparison ?? []).filter((row) =>
        CALC_PLANS.some((p) => p.key === row.plan_code)
      ),
    [report]
  );
  const chosenPlanCode = report?.assumptions?.billing_plan;
  const chosenPlanName = report?.assumptions?.billing_plan_name ?? '—';
  const planAutoSelected = report?.assumptions?.billing_plan_auto_selected ?? true;

  /* Copy for the currently-selected advanced option, shown under its control.
     Explaining the setting you are actually on beats a tooltip on every chip. */
  const planNote = CALC_PLANS.find((p) => p.key === plan)?.info;
  const scenarioNote = CALC_SCENARIOS.find((s) => s.key === scenario)?.info;
  const adoptionNote = CALC_ADOPTIONS.find((a) => a.key === adoption)?.info;
  const bufferNote = CALC_BUFFERS.find((b) => b.key === buffer)?.info;

  if (!slug || !config) {
    return <Navigate to="/calculators/private-practice" replace />;
  }

  const years = report?.projection ?? [];
  /* Where the firm stands today, keyed the same way as a projection year so it
     renders as one more column. Absent on an older backend — the column then
     simply reads "—" and nothing breaks. */
  const baseline = report?.baseline ?? {};
  const hasBaseline = Object.keys(baseline).length > 0;
  const columnCount = years.length + (hasBaseline ? 2 : 1);

  return (
    <main className={styles.page}>
      <SEO
        title={`${config.navLabel} ROI Calculator`}
        description={config.subtitle}
        path={`/calculators/${config.slug}`}
      />
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* ── Hero ── */}
      <div className={styles.heroWrapper}>
        <div className={styles.heroContent}>
          <motion.span
            className={styles.heroLabel}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {CALCULATORS_HERO.label}
          </motion.span>
          <motion.h1
            className={styles.heroTitle}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
          >
            {config.title}
          </motion.h1>
          <motion.p
            className={styles.heroSubtitle}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {config.subtitle}
          </motion.p>

          {/* Profession switcher */}
          <LayoutGroup id="profession-switcher">
            <div className={styles.switcher}>
              {Object.values(CALCULATORS).map((p) => {
                const active = p.slug === config.slug;
                return (
                  <Link
                    key={p.slug}
                    to={`/calculators/${p.slug}`}
                    className={`${styles.switchTab} ${active ? styles.switchTabActive : ''}`}
                  >
                    {active && (
                      <motion.span
                        layoutId="profession-pill"
                        className={styles.switchTabPill}
                        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                      />
                    )}
                    <span className={styles.switchTabLabel}>{p.navLabel}</span>
                  </Link>
                );
              })}
            </div>
          </LayoutGroup>
        </div>
        <div className={styles.heroFade} />
      </div>

      <div className={styles.sectionDivider} />

      {/* ── Calculator card ── */}
      <section className={styles.cardWrap}>
        <LayoutGroup id="calc-card">
        <motion.div className={styles.card} layout transition={CARD_LAYOUT_TRANSITION}>
          {/* Inputs */}
          <motion.form
            className={styles.inputs}
            onSubmit={handleSubmit}
            layout
            transition={CARD_LAYOUT_TRANSITION}
          >
            <span className={styles.badge}>{config.badge}</span>
            <h2 className={styles.formTitle}>{config.formTitle}</h2>

            {config.fields.map((field) => {
              /* With a team breakdown on, the headcount and average rate come
                 from it: shown here read-only so the form never disagrees with
                 the figure actually sent. */
              const fromTeam =
                teamSummary &&
                (field.name === config.team.headcountField ||
                  field.name === config.team.rateField);
              const value = fromTeam
                ? String(
                    field.name === config.team.headcountField
                      ? teamSummary.headcount
                      : Math.round(teamSummary.rate)
                  )
                : form[field.name] ?? '';
              const hint = fromTeam
                ? field.name === config.team.headcountField
                  ? `Counted from your team: ${teamSummary.headcount} people, ${formatValue(teamSummary.fte, 'number', inputCurrency)} full-time equivalent. This replaces any number typed here.`
                  : 'Worked out from your team — the average across the hours each person works.'
                : field.hint;
              return (
              <div className={styles.field} key={field.name}>
                <label className={styles.fieldLabel} htmlFor={field.name}>
                  <span>{field.label}</span>
                  <Info text={field.info} align="down" />
                </label>

                {field.type === 'currency-select' ? (
                  <select
                    id={field.name}
                    className={styles.select}
                    value={form[field.name] ?? ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                  >
                    {currencies.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                ) : field.type === 'currency' ? (
                  <div className={styles.currencyInput}>
                    <span className={styles.currencyPrefix}>{inputCurrency}</span>
                    <input
                      id={field.name}
                      type="number"
                      inputMode="decimal"
                      min={field.min}
                      step={field.step}
                      className={styles.input}
                      value={value}
                      readOnly={!!fromTeam}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                    />
                  </div>
                ) : (
                  <input
                    id={field.name}
                    type="number"
                    inputMode="numeric"
                    min={field.min}
                    step={field.step}
                    className={styles.input}
                    value={value}
                    readOnly={!!fromTeam}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                  />
                )}

                {hint && <span className={styles.fieldHint}>{hint}</span>}

                {field.name === config.team.headcountField && (
                  <>
                    <div className={`${styles.segment} ${styles.teamSwitch}`}>
                      {[
                        { on: false, label: 'Type a number' },
                        { on: true, label: 'Enter each person' },
                      ].map((m) => (
                        <button
                          key={m.label}
                          type="button"
                          className={`${styles.segmentBtn} ${teamOn === m.on ? styles.segmentBtnActive : ''}`}
                          onClick={() => (m.on ? startTeam() : setTeamOn(false))}
                          aria-pressed={teamOn === m.on}
                        >
                          {teamOn === m.on && (
                            <motion.span
                              layoutId="team-mode-pill"
                              className={styles.segmentPill}
                              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                            />
                          )}
                          <span className={styles.segmentLabel}>{m.label}</span>
                        </button>
                      ))}
                    </div>

                    <AnimatePresence initial={false}>
                      {teamOn && team && (
                        <motion.div
                          key="team-editor"
                          className={styles.teamEditor}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                          style={{ overflow: 'hidden' }}
                        >
                          <span className={styles.teamIntro}>
                            {config.team.info}
                          </span>
                          <div className={styles.teamRows}>
                            {team.map((row) => (
                              <div className={styles.teamRow} key={row.id}>
                                <div className={styles.teamRowHead}>
                                  {/* A real dropdown of the usual roles; "Other…"
                                      opens a text box for anything else. */}
                                  <select
                                    className={`${styles.select} ${styles.teamRole}`}
                                    aria-label="Role"
                                    value={
                                      config.team.roles.includes(row.role) ? row.role : OTHER_ROLE
                                    }
                                    onChange={(e) =>
                                      updateRow(row.id, {
                                        role: e.target.value === OTHER_ROLE ? '' : e.target.value,
                                      })
                                    }
                                  >
                                    {config.team.roles.map((r) => (
                                      <option key={r} value={r}>
                                        {r}
                                      </option>
                                    ))}
                                    <option value={OTHER_ROLE}>Other…</option>
                                  </select>
                                  {!config.team.roles.includes(row.role) && (
                                    <input
                                      className={`${styles.input} ${styles.teamRole}`}
                                      placeholder="Role"
                                      aria-label="Other role"
                                      value={row.role}
                                      onChange={(e) => updateRow(row.id, { role: e.target.value })}
                                    />
                                  )}
                                  <button
                                    type="button"
                                    className={styles.teamRemove}
                                    onClick={() => removeRow(row.id)}
                                    aria-label={`Remove ${row.role || 'this row'}`}
                                  >
                                    ×
                                  </button>
                                </div>
                                <div
                                  className={`${styles.teamRowGrid} ${
                                    config.team.rateField ? '' : styles.teamRowGridNoRate
                                  }`}
                                >
                                  <label className={styles.teamCell}>
                                    <span className={styles.teamCellLabel}>People</span>
                                    <input
                                      className={styles.input}
                                      type="number"
                                      inputMode="numeric"
                                      min={1}
                                      step={1}
                                      value={row.count}
                                      onChange={(e) => updateRow(row.id, { count: e.target.value })}
                                    />
                                  </label>
                                  <label className={styles.teamCell}>
                                    <span className={styles.teamCellLabel}>Works</span>
                                    <select
                                      className={styles.select}
                                      value={row.days}
                                      onChange={(e) =>
                                        updateRow(row.id, { days: Number(e.target.value) })
                                      }
                                    >
                                      {WORK_PATTERNS.map((w) => (
                                        <option key={w.days} value={w.days}>
                                          {w.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  {config.team.rateField && (
                                    <label className={styles.teamCell}>
                                      <span className={styles.teamCellLabel}>
                                        {config.team.rateLabel}
                                      </span>
                                      <input
                                        className={styles.input}
                                        type="number"
                                        inputMode="decimal"
                                        min={0}
                                        placeholder={form[config.team.rateField]}
                                        value={row.rate}
                                        onChange={(e) => updateRow(row.id, { rate: e.target.value })}
                                      />
                                    </label>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className={styles.teamActions}>
                            <button
                              type="button"
                              className={styles.planChip}
                              onClick={() =>
                                setTeam((rows) => [
                                  ...(rows ?? []),
                                  newRow({
                                    role:
                                      config.team.roles[(rows ?? []).length] ??
                                      config.team.roles[config.team.roles.length - 1],
                                  }),
                                ])
                              }
                            >
                              + Add people
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </div>
              );
            })}

            <button
              type="button"
              className={styles.advancedToggle}
              onClick={() => setAdvancedOpen((v) => !v)}
              aria-expanded={advancedOpen}
            >
              <span>{advancedOpen ? '− Hide' : '+ Show'} advanced options</span>
            </button>

            <AnimatePresence initial={false}>
              {advancedOpen && (
                <motion.div
                  key="advanced-panel"
                  className={styles.advancedPanel}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className={styles.advancedInner}>
                    <div className={styles.advField}>
                      <div className={styles.advHeader}>
                        <span className={styles.advLabel}>
                          Modules you will use
                          <Info text={CALCULATORS_HERO.modulesInfo} align="down" />
                        </span>
                        <span className={styles.advHint}>{CALCULATORS_HERO.modulesHint}</span>
                      </div>
                      <div className={styles.moduleGrid}>
                        {CALC_MODULES.map((m) => {
                          const on = modules.includes(m.key);
                          return (
                            <div
                              key={m.key}
                              className={`${styles.moduleChip} ${on ? styles.moduleChipOn : ''}`}
                            >
                              <button
                                type="button"
                                className={styles.moduleToggle}
                                onClick={() => toggleModule(m.key)}
                                aria-pressed={on}
                              >
                                <span className={styles.moduleCheck}>{on ? '✓' : ''}</span>
                                <span className={styles.moduleText}>
                                  <span className={styles.moduleName}>{m.label}</span>
                                  <span className={styles.moduleHint}>{m.hint}</span>
                                </span>
                              </button>
                              <Info text={m.info} align="down" />
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className={styles.advField}>
                      <div className={styles.advHeader}>
                        <span className={styles.advLabel}>
                          Your monthly usage
                          <Info
                            text="How much work you put through JurisTab in a month, per person. This drives the cost side only — it never changes the time you get back. The figures below start at a typical workload for your profession."
                            align="down"
                          />
                        </span>
                        <span className={styles.advHint}>
                          Per professional, per month. This is what drives the cost side —
                          pages past your allowance, and the actions your plan does not
                          make unlimited. Pre-filled with typical volumes.
                        </span>
                      </div>
                      <div className={styles.usageGrid}>
                        {config.usageFields.map((field) => (
                          <div className={styles.usageField} key={field.name}>
                            <label className={styles.usageLabel} htmlFor={field.name}>
                              <span>{field.label}</span>
                              <Info text={field.info} align="down" />
                            </label>
                            <input
                              id={field.name}
                              type="number"
                              inputMode="numeric"
                              min={field.min}
                              step={field.step}
                              className={styles.input}
                              value={form[field.name] ?? ''}
                              onChange={(e) => handleChange(field.name, e.target.value)}
                            />
                            {field.hint && (
                              <span className={styles.fieldHint}>{field.hint}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className={styles.advField}>
                      <div className={styles.advHeader}>
                        <span className={styles.advLabel}>
                          Plan
                          <Info
                            text="Which JurisTab plan the report prices you on. Left alone, we cost all five at your volumes and use whichever is cheapest — the same comparison your in-app dashboard runs each month."
                            align="down"
                          />
                        </span>
                        <span className={styles.advHint}>
                          Default: “Cheapest for me” — we price every plan at your volume and
                          pick the one that costs least.
                        </span>
                      </div>
                      <div className={styles.planGrid}>
                        {CALC_PLANS.map((p) => (
                          <button
                            key={p.key}
                            type="button"
                            className={`${styles.planChip} ${plan === p.key ? styles.planChipOn : ''}`}
                            onClick={() => setPlan(p.key)}
                            aria-pressed={plan === p.key}
                            title={p.info}
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                      {planNote && <span className={styles.advNote}>{planNote}</span>}
                    </div>

                    <div className={styles.advField}>
                      <div className={styles.advHeader}>
                        <span className={styles.advLabel}>
                          Safety margin on the savings
                          <Info
                            text="A deliberate haircut taken off the savings — and never off the cost — before anything is shown, so the number is one you can defend rather than the best case the model can produce. Starts at 15%, meaning you are shown 85% of what the model works out."
                            align="down"
                          />
                        </span>
                        <span className={styles.advHint}>
                          Default: 15% held back. Applied to the savings only — never to
                          what JurisTab costs you.
                        </span>
                      </div>
                      <div className={styles.segment}>
                        {CALC_BUFFERS.map((b) => (
                          <button
                            key={b.key}
                            type="button"
                            className={`${styles.segmentBtn} ${buffer === b.key ? styles.segmentBtnActive : ''}`}
                            onClick={() => setBuffer(b.key)}
                            title={b.info}
                          >
                            {buffer === b.key && (
                              <motion.span
                                layoutId="buffer-pill"
                                className={styles.segmentPill}
                                transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                              />
                            )}
                            <span className={styles.segmentLabel}>{b.label}</span>
                          </button>
                        ))}
                      </div>
                      {bufferNote && <span className={styles.advNote}>{bufferNote}</span>}
                    </div>

                    <div className={styles.advField}>
                      <div className={styles.advHeader}>
                        <span className={styles.advLabel}>
                          Savings confidence
                          <Info
                            text="How much time we assume each module actually gives back. Conservative takes 70% of the modelled saving, optimistic 25% more. Starts on Expected — the middle case."
                            align="down"
                          />
                        </span>
                        <span className={styles.advHint}>
                          How conservative are the assumed time savings? Default: Expected.
                        </span>
                      </div>
                      <div className={styles.segment}>
                        {CALC_SCENARIOS.map((s) => (
                          <button
                            key={s.key}
                            type="button"
                            className={`${styles.segmentBtn} ${scenario === s.key ? styles.segmentBtnActive : ''}`}
                            onClick={() => setScenario(s.key as Scenario)}
                            title={s.info}
                          >
                            {scenario === s.key && (
                              <motion.span
                                layoutId="scenario-pill"
                                className={styles.segmentPill}
                                transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                              />
                            )}
                            <span className={styles.segmentLabel}>{s.label}</span>
                          </button>
                        ))}
                      </div>
                      {scenarioNote && <span className={styles.advNote}>{scenarioNote}</span>}
                    </div>

                    <div className={styles.advField}>
                      <div className={styles.advHeader}>
                        <span className={styles.advLabel}>
                          Adoption ramp
                          <Info
                            text="Nobody gets the full benefit on day one. This is how long your team takes to work the way the tools expect — it holds Year 1 back, and every option reaches full strength by the end of Year 1, so Years 2 and 3 get the full benefit. Starts at six months, a typical rollout."
                            align="down"
                          />
                        </span>
                        <span className={styles.advHint}>
                          Time for your team to reach full productivity (Year 1 only).
                          Default: 6 months.
                        </span>
                      </div>
                      <div className={styles.segment}>
                        {CALC_ADOPTIONS.map((a) => (
                          <button
                            key={a.key}
                            type="button"
                            className={`${styles.segmentBtn} ${adoption === a.key ? styles.segmentBtnActive : ''}`}
                            onClick={() => setAdoption(a.key)}
                            title={a.info}
                          >
                            {adoption === a.key && (
                              <motion.span
                                layoutId="adoption-pill"
                                className={styles.segmentPill}
                                transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                              />
                            )}
                            <span className={styles.segmentLabel}>{a.label}</span>
                          </button>
                        ))}
                      </div>
                      {adoptionNote && <span className={styles.advNote}>{adoptionNote}</span>}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <button type="submit" className={styles.calcButton} disabled={loading}>
              {loading ? 'Calculating…' : 'Calculate ROI'}
            </button>
          </motion.form>

          {/* Results */}
          <motion.div className={styles.results} layout transition={CARD_LAYOUT_TRANSITION}>
            {error ? (
              <div className={styles.errorBox}>
                <p>{error}</p>
                <button
                  className={styles.retryBtn}
                  onClick={() =>
                    runCalc(config, form, { scenario, adoption, plan, modules, buffer, team: activeTeam })
                  }
                >
                  Try again
                </button>
              </div>
            ) : (
              <>
                <div className={styles.resultsHeader}>
                  <span className={styles.resultsLabel}>
                    {config.headlineLabel}
                    <Info text={config.headlineInfo} align="down" />
                  </span>
                  <AnimatePresence>
                    {!revealed && years.length > 1 && (
                      <motion.button
                        key="reveal-header"
                        className={styles.revealBtn}
                        onClick={() => setRevealed(true)}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        Reveal Full Results
                      </motion.button>
                    )}
                  </AnimatePresence>
                </div>

                <div className={styles.headlineNumber}>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={`headline-${revision}`}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -14 }}
                      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                      style={{ display: 'inline-block' }}
                    >
                      {headlineMultiple !== null ? formatValue(headlineMultiple, 'multiple', currency) : '—'}
                    </motion.span>
                  </AnimatePresence>
                </div>

                {/* Support metric chips */}
                <motion.div
                  className={styles.metricChips}
                  key={`chips-${revision}`}
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: { staggerChildren: 0.08, delayChildren: 0.08 },
                    },
                  }}
                >
                  {config.supportMetrics.map((m) => (
                    <motion.div
                      className={styles.chip}
                      key={m.key}
                      variants={{
                        hidden: { opacity: 0, y: 12 },
                        visible: { opacity: 1, y: 0 },
                      }}
                      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                    >
                      <span className={styles.chipValue}>
                        {formatValue(
                          typeof report?.headline[m.key] === 'number'
                            ? (report?.headline[m.key] as number)
                            : null,
                          m.format,
                          currency
                        )}
                      </span>
                      <span className={styles.chipLabel}>
                        {m.label}
                        <Info text={m.info} />
                      </span>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Which plan you should actually be on, and what the
                    alternatives would have cost at the same volume. */}
                {planComparison.length > 0 && (
                  <motion.div
                    className={styles.planPanel}
                    key={`plan-${revision}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                    layout
                  >
                    <div className={styles.planPanelHead}>
                      <span className={styles.planPanelLabel}>
                        {planAutoSelected ? 'Cheapest plan at your volume' : 'Costed on'}
                        <Info text="Every plan priced at the volumes you entered, cheapest first. The highlighted one is what the report above is costed on — change it under advanced options if you want to see another." />
                      </span>
                      <span className={styles.planPanelName}>{chosenPlanName}</span>
                    </div>
                    <ul className={styles.planCompareList}>
                      {planComparison.map((row) => {
                        const active = row.plan_code === chosenPlanCode;
                        return (
                          <li
                            key={row.plan_code}
                            className={`${styles.planCompareRow} ${active ? styles.planCompareRowActive : ''}`}
                          >
                            <span className={styles.planCompareName}>
                              {row.plan_name}
                              <Info
                                text={
                                  CALC_PLANS.find((p) => p.key === row.plan_code)?.info ??
                                  'Annual cost of this plan at the volumes you entered.'
                                }
                              />
                            </span>
                            <span className={styles.planCompareCost}>
                              {formatValue(row.total_cost, 'money', currency)}
                              <span className={styles.planComparePer}> / yr</span>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    <p className={styles.planPanelNote}>
                      Subscription plus metered usage at steady state, on annual billing —
                      12 months for the price of 10. Prices cover one practice area; each extra
                      area adds 33% (none on Case Management).
                    </p>
                  </motion.div>
                )}

                {/* Projection table — today first, then each projected year. */}
                <motion.div className={styles.tableWrap} layout transition={CARD_LAYOUT_TRANSITION}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.rowHeadCell}></th>
                        {hasBaseline && (
                          <th className={`${styles.yearCell} ${styles.baselineCell}`}>
                            <span className={styles.yearCellInner}>
                              {config.baselineLabel}
                              <Info text={config.baselineInfo} />
                            </span>
                          </th>
                        )}
                        {years.map((y) => (
                          <th key={y.year} className={styles.yearCell}>
                            Year {y.year}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {config.rows.map((row, rowIdx) => {
                        const prevGroup = rowIdx > 0 ? config.rows[rowIdx - 1].group : undefined;
                        const showGroup = !!row.group && row.group !== prevGroup;
                        return (
                          <React.Fragment key={row.key}>
                            {showGroup && (
                              <tr className={styles.groupRow}>
                                <td className={styles.groupCell} colSpan={columnCount}>
                                  {row.group}
                                </td>
                              </tr>
                            )}
                            <tr className={row.emphasis ? styles.emphasisRow : ''}>
                              <td className={styles.rowHead}>
                                <span>{row.label}</span>
                                <Info text={row.info} />
                              </td>
                              {hasBaseline && (
                                <td className={`${styles.valueCell} ${styles.baselineCell}`}>
                                  {formatValue(
                                    baseline[row.key] as number | undefined,
                                    row.format,
                                    currency
                                  )}
                                </td>
                              )}
                              {years.map((y, idx) => {
                                const blurred = idx > 0 && !revealed;
                                return (
                                  <td
                                    key={y.year}
                                    className={`${styles.valueCell} ${blurred ? styles.blurred : ''}`}
                                  >
                                    <AnimatePresence mode="wait" initial={false}>
                                      <motion.span
                                        key={`${revision}-${y.year}-${row.key}`}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                                        style={{ display: 'inline-block' }}
                                      >
                                        {formatValue(y[row.key], row.format, currency)}
                                      </motion.span>
                                    </AnimatePresence>
                                  </td>
                                );
                              })}
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>

                  <AnimatePresence>
                    {!revealed && years.length > 1 && (
                      <motion.button
                        key="reveal-overlay"
                        className={styles.revealOverlayBtn}
                        onClick={() => setRevealed(true)}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                      >
                        <FiLock /> Reveal Year 2 &amp; Year 3
                      </motion.button>
                    )}
                  </AnimatePresence>
                </motion.div>

                {report?.fx_note && (
                  <p className={styles.fxNote}>{report.fx_note}</p>
                )}
                <p className={styles.disclaimer}>
                  {report?.disclaimer}{' '}
                  {buffer > 0 &&
                    `The savings shown are already reduced by a ${Math.round(
                      buffer * 100
                    )}% safety margin; the cost of JurisTab is not.`}
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
        </LayoutGroup>

        {/* CTA */}
        <div className={styles.ctaBar}>
          <div>
            <h3 className={styles.ctaTitle}>Turn these numbers into reality</h3>
            <p className={styles.ctaText}>
              Start using JurisTab with your team and capture the regained capacity above.
            </p>
          </div>
          <a className={styles.ctaButton} href={REGION.apps.juristab} target="_blank" rel="noopener noreferrer">
            Get started <FiArrowRight />
          </a>
        </div>
      </section>
    </main>
  );
};

export default Calculators;
